import { ChangeEvent, useContext, useState } from "react";
import { defaultKursusholdDeltagereColumns } from "../../../../../config/kursusConfig";
import {
  createColumnEditorTrigger,
  getHeaderActiveColumns,
  TD,
} from "../../../../../util/table";
import { ModalProvider, ModalTrigger } from "../../../../Modal/Modal";
import { TableBody, TableHead, TableRow } from "../../../../Table/TableBasic";
import OpretNyButtonBlock from "../../../person/id/OpretNyButtonBlock";
import { Checkbox } from "../../../../formcontrols/InputCheckbox";
import useActiveColumns from "../../../../../hooks/useActiveColumns";
import KursusDeltagereHandlingerDropdown from "./KursusDeltagereHandlingerDropdown";
import { kursusDeltagereFormConfig } from "../../../../../config/form/kursus/kursusDeltagere/KursusDeltagereFormConfig";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallback from "../../../../ErrorFallback";
import useLocalStorageState from "use-local-storage-state";
import { DropdownMenuItem } from "../../../../dropdown/DropdownMenu";
import ModalCustom from "../../../../Modal/modals/ModalCustom";
import { kursusDeltagereMoveFormConfig } from "../../../../../config/form/kursus/kursusDeltagere/KursusDeltagereMoveFormConfig";
import { kursusDeltagereCancelFormConfig } from "../../../../../config/form/kursus/kursusDeltagere/kursusDeltagereCancelFormConfig";
import UsersIcon from "../../../../icons/UsersIcon";
import { KursusholdPersonKursistField } from "../../../../../interfaces/kursus";
import {
  FakturaStatusId,
  DeltagerStatusId,
  Firma,
  KursusPerson,
  Kursushold,
} from "../../../../../interfaces/schema";
import Spinner from "../../../../spinner/Spinner";
import { ConfigKursusGruppe } from "../../../../../constants/config";
import { useQuery } from "@apollo/client";
import { GET_DELTAGER_BY_KURSUSID_QUERY } from "../../../../../api/kursus/queries";
import DeltagerFiltre from "./DeltagerFiltre";
import { SortBy } from "../../../../../interfaces/table";
import { format } from "date-fns";
import { post } from "../../../../../api/util";
import { tableToXLSX } from "../../../../../util/tableExportUtils";
import {
  DropdownItem,
  DropdownItemIcon,
  DropdownSeparator,
} from "../../../../dropdown/Dropdown";
import EmailIcon from "../../../../icons/EmailIcon";
import { UserQueryContext } from "../../../../../context/UserContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../../../../select/Select";
import EditIcon from "../../../../icons/EditIcon";
import UserMoveIcon from "../../../../icons/UserMoveIcon";
import UserRemoveIcon from "../../../../icons/UserRemoveIcon";
import AlertRoot, { Alert } from "../../../../Alert/Alert";
import Button from "@/components/bricks/button/Button";
import { useMutation } from "@tanstack/react-query";
import { orderBy } from "../../../../../util/orderBy";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";

export default function KursusDeltagere({
  id,
  kursushold,
}: {
  id: string;
  kursushold: Kursushold;
}) {
  // mutation
  const {
    mutate: sendFaktura,
    isPending: isLoading,
    reset,
    // error
  } = useMutation({
    mutationFn: async () => {
      await post({
        query: `kursus/${id}/wizards/fakturer`,
        body: {
          kursusPersonIds: selectedIkkeFaktureretIds,
        },
      });
    },
    onSuccess: () => {
      refetch();
      setFakturaAlertOpen(false);
      reset();
    },
  });
  const [fakturaAlertOpen, setFakturaAlertOpen] = useState(false);

  // context
  const { user, hasPermission } = useContext(UserQueryContext);
  const canWrite = hasPermission(user, "KursusWrite");

  // state
  const [filter, setFilter] = useState(initialFilterState);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [activeModal, setActiveModal] = useState<"edit" | "move" | "cancel">(
    "edit",
  );
  const [deltagerId, setDeltagerId] = useState<string | null>(null);

  // localstorage state
  const [, setTabs] = useLocalStorageState("kursus-id-tabs");
  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "kursusDeltagereSortBy",
    {
      defaultValue: defaultSortBy,
    },
  );

  // hooks and state
  const [kursusholdDeltagereColumns, setKursusholdDeltagereActiveColumns] =
    useActiveColumns(
      "kursus-kursusholddeltagere-active-columns",
      defaultKursusholdDeltagereColumns,
    );

  // queries
  // fetch deltager by id
  const {
    data: kursusData,
    loading,
    error: kursusError,
    refetch,
  } = useQuery<{
    kursushold: {
      kursusPerson: KursusPerson[];
    };
  }>(GET_DELTAGER_BY_KURSUSID_QUERY(), {
    variables: { id, dato: format(new Date(), "yyyy-MM-dd") },
  });

  if (loading) {
    return <Spinner label="Henter kursusdeltagere ..." />;
  }

  if (kursusError) {
    return <div>Der skete en fejl ved indhentning af kursusdeltagere ...</div>;
  }

  const configs = {
    editTrigger:
      kursushold.kursustype === 5 ? null : (
        <DropdownMenuItem asChild key="edit">
          <ModalTrigger onClick={() => setActiveModal("edit")}>
            <span className="text-primary">
              <EditIcon />
            </span>{" "}
            Rediger deltager
          </ModalTrigger>
        </DropdownMenuItem>
      ),
  };

  // now we doin' business
  const kursusPersoner = kursusData?.kursushold.kursusPerson || [];

  const filteredKursusPersoner = filterKursusPersoner(kursusPersoner, filter);
  const selectedKursusPersoner = filteredKursusPersoner.filter((kp) =>
    selectedIds.some((id) => kp.id === id),
  );
  const selectedKursusPersonerIds = selectedKursusPersoner.map((kp) => kp.id);
  const selectedPersonIds = selectedKursusPersoner.map((kp) => kp.personId);
  const groupData = selectedKursusPersoner.map(
    (kp) => kp.kursusPersonKursusGruppe,
  );
  const selectedIkkeFaktureretIds = selectedKursusPersoner
    .filter((kp) => {
      if (kp.deltagerStatus === DeltagerStatusId.Afmeldt) {
        return false;
      }
      if (kp.fakturaStatusNavigation?.navn === FakturaStatusId.Faktureret) {
        return false;
      }
      return true;
    })
    .map((kp) => kp.id);

  const selectedEmailAblePersonIds = selectedKursusPersoner.flatMap((kp) => {
    if (kp.person.prioriteretEmail) {
      return [kp.personId];
    } else {
      return [];
    }
  });

  const allSelected = selectedIds.length === filteredKursusPersoner.length;

  // check if selected can be invoiced
  const canInvoiceSelected = selectedKursusPersoner.some((kp) => {
    // check kursustype
    // kursustyper - 1: tema, 3: FMU, 5: FMU Prøve
    const kursusTyper = [1, 3, 5];
    if (!kursusTyper.includes(kursushold.kursustype)) {
      return false;
    }

    // check start date is in the future
    if (new Date(kursushold.fraDato) < new Date()) {
      return false;
    }

    // check participant status
    if (kp.deltagerStatus !== DeltagerStatusId.Tilmeldt) {
      return false;
    }

    // check invoice status
    if (kp.fakturaStatus !== FakturaStatusId.IngenStatus) {
      return false;
    }

    return true;
  });

  // handlers
  async function handleCheckboxChange(
    i: number,
    event: ChangeEvent<HTMLInputElement>,
  ) {
    const checked = event.target.checked;
    try {
      await post({
        query: `${`kursus/${id}/gruppe`}/${i}/${
          !checked ? "fjern" : "tilfoej"
        }`,
        body: selectedKursusPersonerIds,
      });
      await refetch();
    } catch (error) {
      console.error(error);
    }
  }

  function handleDownloadToExcel() {
    tableToXLSX(kursusholdDeltagereColumns, selectedKursusPersoner, {
      fileName: `kursus-deltagere-export`,
    });
  }

  return (
    <div className="container h-100 overflow-hidden">
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onReset={() => setTabs("stamoplysninger")}
      >
        <div className="c-card p-0 h-100 overflow-hidden d-flex flex-column">
          <div className="py-3 px-4 d-flex justify-content-between align-items-center">
            <h3 className="d-flex align-items-center gap-2">
              <span className="c-theme-icon bg-primary-light text-primary">
                <UsersIcon />
              </span>
              Deltagere
              <span className="text-muted fw-medium">
                {selectedKursusPersoner.length > 0
                  ? selectedKursusPersoner.length + " ud af "
                  : ""}
                {filteredKursusPersoner.length !== kursusPersoner.length
                  ? filteredKursusPersoner.length +
                    ` (${kursusPersoner.length})`
                  : kursusPersoner.length}
              </span>
            </h3>
            <div className="d-flex gap-3">
              <DeltagerFiltre
                filter={filter}
                setFilter={setFilter}
                setSelectedIds={setSelectedIds}
              />
              <KursusDeltagereHandlingerDropdown
                badgeSolid
                typeTitle="deltagere"
                allSelected={allSelected}
                selectedIds={selectedPersonIds}
                totalCount={selectedIds.length}
                emailAbleIds={selectedEmailAblePersonIds}
                type="kursus"
                typeId={id}
                onDownloadToExcel={handleDownloadToExcel}
              >
                <>
                  <DropdownSeparator />
                  <DropdownItem
                    disabled={!canInvoiceSelected}
                    onClick={
                      canInvoiceSelected
                        ? () => setFakturaAlertOpen(true)
                        : undefined
                    }
                  >
                    <DropdownItemIcon>
                      <EmailIcon />
                    </DropdownItemIcon>
                    <span>Send faktura ...</span>
                  </DropdownItem>

                  {/* not implemented yet */}
                  <DropdownItem
                    className="todo"
                    disabled={true}
                    onClick={() => {}}
                  >
                    <DropdownItemIcon>
                      <EmailIcon />
                    </DropdownItemIcon>
                    <span className="todo">Send eksamensbevis...</span>
                  </DropdownItem>
                  <DropdownSeparator />
                  <div className="p-2 pt-1">
                    <h6 className="form-label mb-3">Tildel gruppe</h6>
                    <div
                      className="d-grid gap-2"
                      style={{ gridTemplateColumns: "1fr 1fr" }}
                    >
                      {Object.values(ConfigKursusGruppe.badge.className).map(
                        (className, i) => {
                          const newState = {};

                          if (groupData) {
                            const totalArrays = groupData.length;
                            const groupCount = {};

                            groupData.forEach((array) => {
                              array.forEach((item) => {
                                const nr = item.kursusGruppeNr;
                                groupCount[nr] = (groupCount[nr] || 0) + 1;
                              });
                            });

                            Object.keys(groupCount).forEach((nr) => {
                              if (groupCount[nr] === totalArrays) {
                                newState[nr] = "CHECKED";
                              } else {
                                newState[nr] = "INDETERMINATE";
                              }
                            });
                          }

                          const state = newState[i + 1];
                          const checkboxState = state ?? "EMPTY";

                          return (
                            <div key={`Gruppe ${i + 1}`}>
                              <label className="d-flex gap-2 align-items-center">
                                <Checkbox
                                  checkboxState={checkboxState}
                                  onChange={(e) =>
                                    handleCheckboxChange(i + 1, e)
                                  }
                                  className="form-check-input mt-0"
                                />
                                <span className={className}>
                                  Gruppe {i + 1}
                                </span>
                              </label>
                            </div>
                          );
                        },
                      )}
                    </div>
                  </div>
                  {kursushold.kursustype === 5 && (
                    <div className="p-2 pt-1">
                      <h6 className="form-label mb-3">Eksamensstatus</h6>
                      <Select
                        onValueChange={async (value) => {
                          try {
                            await post({
                              query: `kursus/${kursushold.id}/wizards/bestaaetstatus`,
                              body: {
                                kursusPersonIds: selectedKursusPersoner.map(
                                  (p) => p.id,
                                ),
                                bestaaetStatus: value,
                              },
                            });
                            await refetch();
                          } catch (error) {
                            console.error(error);
                          }
                        }}
                      >
                        <SelectTrigger
                          ariaLabel="Eksamensstatus"
                          placeholder="Vælg..."
                        />
                        <SelectContent>
                          {[
                            { text: "Bestået", value: "Ja" },
                            { text: "Ikke bestået", value: "Nej" },
                            { text: "Syg", value: "Syg" },
                          ].map((e) => (
                            <SelectItem key={e.value} value={e.value}>
                              {e.text}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              </KursusDeltagereHandlingerDropdown>
            </div>
          </div>
          <ModalProvider>
            <div className="px-2 flex-grow-1 table-scroll">
              <table className="table table--selectable table--selectable-embedded">
                <TableHead ColumnEditorTrigger={createColumnEditorTrigger()}>
                  <th className="action-select">
                    <label className="form-check-label--wrapped">
                      <Checkbox
                        checkboxState={
                          allSelected
                            ? "CHECKED"
                            : selectedIds.length
                              ? "INDETERMINATE"
                              : "EMPTY"
                        }
                        aria-label="Vælg"
                        type="checkbox"
                        className="form-check-input"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds(
                              filteredKursusPersoner.map((pk) => pk.id),
                            );
                          } else {
                            setSelectedIds([]);
                          }
                        }}
                      />
                    </label>
                  </th>
                  <th scope="col">Gruppe</th>
                  <>
                    {getHeaderActiveColumns(kursusholdDeltagereColumns, {
                      defaultSortBy,
                      setSortBy,
                      sortBy,
                    })}
                  </>
                </TableHead>
                <TableBody>
                  <ModalProvider>
                    {orderBy(
                      filteredKursusPersoner,
                      sortBy[0].field,
                      sortBy[0].direction,
                    ).map((deltager, i) => {
                      const checked = selectedIds.indexOf(deltager.id) > -1;
                      return (
                        <TableRow
                          action={() => setDeltagerId(deltager.id)}
                          key={i}
                          invalidateData={refetch}
                          className={`${checked ? "selected" : ""}`}
                          onClick={() => {}}
                          moreActions={
                            deltager.deltagerStatus !== "AFMELDT" && canWrite
                              ? [
                                  configs.editTrigger,
                                  <DropdownMenuItem asChild key="move">
                                    <ModalTrigger
                                      onClick={() => setActiveModal("move")}
                                    >
                                      <span className="text-primary d-flex align-items-center">
                                        <UserMoveIcon />
                                      </span>{" "}
                                      Flyt deltager
                                    </ModalTrigger>
                                  </DropdownMenuItem>,
                                  <DropdownMenuItem asChild key="cancel">
                                    <ModalTrigger
                                      onClick={() => setActiveModal("cancel")}
                                    >
                                      <span className="text-primary d-flex align-items-center">
                                        <UserRemoveIcon />
                                      </span>{" "}
                                      Afmeld deltager
                                    </ModalTrigger>
                                  </DropdownMenuItem>,
                                ]
                              : []
                          }
                        >
                          <>
                            <th
                              scope="row"
                              className="action-select"
                              onClick={(event: React.MouseEvent) => {
                                event.stopPropagation();
                              }}
                            >
                              <label
                                className="form-check-label--wrapped"
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                <Checkbox
                                  checkboxState={checked ? "CHECKED" : "EMPTY"}
                                  aria-label="Vælg"
                                  type="checkbox"
                                  className="form-check-input"
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    if (e.target.checked) {
                                      setSelectedIds([
                                        ...selectedIds,
                                        deltager.id,
                                      ]);
                                    } else {
                                      setSelectedIds(
                                        selectedIds.filter(
                                          (id) => id !== deltager.id,
                                        ),
                                      );
                                    }
                                  }}
                                />
                              </label>
                            </th>
                            <td>
                              <div className="small d-flex gap-1">
                                {deltager.kursusPersonKursusGruppe
                                  .sort(
                                    (a, b) =>
                                      a.kursusGruppeNr - b.kursusGruppeNr,
                                  )
                                  .map((p) => {
                                    return (
                                      <span
                                        key={p.kursusGruppeNr}
                                        className={
                                          ConfigKursusGruppe.badge.className[
                                            p.kursusGruppeNr
                                          ]
                                        }
                                      >
                                        G{p.kursusGruppeNr}
                                      </span>
                                    );
                                  })}
                              </div>
                            </td>
                            {kursusholdDeltagereColumns
                              .filter((col) => col.active)
                              .map((col) => {
                                return (
                                  <TD
                                    key={col.field}
                                    col={col}
                                    rowData={deltager}
                                  />
                                );
                              })}
                          </>
                        </TableRow>
                      );
                    })}
                    {activeModal === "edit" &&
                      deltagerId &&
                      kursushold.kursustype !== 5 && (
                        <ModalCustom
                          config={kursusDeltagereFormConfig(
                            kursushold.kursustype,
                            id,
                            deltagerId,
                          )}
                          title="Rediger deltager"
                          method="patch"
                          invalidateData={refetch}
                        />
                      )}
                    {activeModal === "move" && deltagerId && (
                      <ModalCustom
                        config={kursusDeltagereMoveFormConfig(id, deltagerId)}
                        title="Flyt deltager"
                        icon={<UserMoveIcon height={22} />}
                        method="post"
                        invalidateData={refetch}
                        treatAsPatch
                      />
                    )}
                    {activeModal === "cancel" && deltagerId && (
                      <ModalCustom
                        config={kursusDeltagereCancelFormConfig(id, deltagerId)}
                        icon={<UserRemoveIcon height={22} />}
                        title="Afmeld deltager"
                        method="post"
                        invalidateData={refetch}
                      />
                    )}
                  </ModalProvider>
                </TableBody>
              </table>
              <ColumnEditorModal
                defaultColumns={defaultKursusholdDeltagereColumns}
                columns={kursusholdDeltagereColumns}
                setActiveColumns={setKursusholdDeltagereActiveColumns}
              />
            </div>
            <div className="flex-shrink-0 border-top bg-white shadow">
              <OpretNyButtonBlock
                className="d-grid d-md-flex justify-content-md-end p-1 pe-3"
                editConfig={kursusDeltagereFormConfig(
                  kursushold.kursustype,
                  id,
                )}
                invalidateData={refetch}
              />
            </div>
          </ModalProvider>
        </div>
        <AlertRoot
          open={fakturaAlertOpen}
          setOpen={(open) => setFakturaAlertOpen(open)}
        >
          <Alert
            title="Send faktura"
            description={`Du har valg at fakturere ${selectedIkkeFaktureretIds?.length} kursusdeltager${
              selectedIkkeFaktureretIds?.length === 1 ? "" : "e"
            }. Fakturer?`}
            cancelButton={
              <Button color="secondary" onClick={() => {}}>
                Nej, gå tilbage
              </Button>
            }
            actionButton={
              <Button
                disabled={isLoading}
                onClick={() => {
                  sendFaktura();
                }}
              >
                Ja, send faktura
              </Button>
            }
          ></Alert>
        </AlertRoot>
      </ErrorBoundary>
    </div>
  );
}

const defaultSortBy: SortBy<KursusholdPersonKursistField>[] = [
  {
    direction: "asc",
    field: "person.fornavne",
  },
];
export type IKundenummerFilterNrType =
  "Mellem" | "Mindre end" | "Større end" | "Lig med";
interface IKundenummer {
  type: IKundenummerFilterNrType;
  input: string;
}

type KursusFilterDateType = "Mellem" | "Før" | "Efter";
interface IKursusDateFilter {
  type: KursusFilterDateType;
  date: Date | null;
  date2?: Date | null;
}

type DeltagerStatus = "TILMELDT" | "AFMELDT";
export interface KursusFilterState {
  nr: IKundenummer[];
  date: IKursusDateFilter;
  groups: number[];
  status: DeltagerStatus[];
}
const initialFilterState: KursusFilterState = {
  nr: [],
  date: { type: "Mellem", date: null, date2: null },
  groups: [],
  status: ["TILMELDT"],
};

function filterKursusPersoner(
  kursusPerson: KursusPerson[],
  filter: KursusFilterState,
) {
  const fkp = kursusPerson
    // filter on condition 1 - group filter
    .filter((p) => {
      if (filter.groups.length === 0) {
        return true;
      }

      return filter.groups.every((groupNr) =>
        p.kursusPersonKursusGruppe.some(
          (group) => group.kursusGruppeNr === groupNr,
        ),
      );
    })

    // filter on condition 2 - kundenummer filter
    .filter((p) => {
      return (
        filter.nr.length === 0 ||
        filter.nr.some(({ type, input }) => {
          const kundeNr = Number(p.person.kundeNr);
          const inputNum = Number(input);

          switch (type) {
            case "Lig med":
              return kundeNr === inputNum;
            case "Større end":
              return kundeNr >= inputNum;
            case "Mindre end":
              return kundeNr <= inputNum;
            case "Mellem":
              const [lowerBound, upperBound] = input.split("-").map(Number);
              return kundeNr >= lowerBound && kundeNr <= upperBound;
            default:
              return true;
          }
        })
      );
    })

    // filter on condition 3 - date filter
    .filter((p) => {
      if (!filter.date.date) return true;
      switch (filter.date.type) {
        case "Efter":
          return new Date(p.tilmeldDato) > filter.date.date;
        case "Før":
          return new Date(p.tilmeldDato) < filter.date.date;
        case "Mellem":
          if (!filter.date.date2) return true;
          return (
            new Date(p.tilmeldDato) > filter.date.date &&
            new Date(p.tilmeldDato) < filter.date.date2
          );
        default:
          return true;
      }
    })
    // filter on condition  - status filter
    .filter((p) => {
      if (!filter.status.length || !p.deltagerStatus) return true;
      const status: DeltagerStatus = p.deltagerStatus as DeltagerStatus;
      return filter.status.includes(status);
    });

  if (fkp.length) {
    for (const p of fkp) {
      const pId = p.person.primaerPersonFirmaRelationPaa?.firma.id;

      if (p.person.primaerPersonFirmaRelationPaa && !pId) {
        const firma: Firma = p.person.personFirma.filter(
          (f) => f.firma.id === pId,
        )[0].firma;
        p.person.primaerPersonFirmaRelationPaa.firma.fakturaAfsendelse =
          firma.fakturaAfsendelse;
        p.person.primaerPersonFirmaRelationPaa.emailArbejde = firma.email;
      }
    }
  }

  return fkp;
}
